import { useState } from 'react'
import Cookies from 'js-cookie'
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSliceDispatch, useSliceStateSelector } from 'slices/hooks'
import { CookieConsent } from 'slices/Cookies/types'
import { cookieName } from 'utils/cookies'
import { useTranslation } from 'react-i18next'

const CookieSettings = () => {
  const cookieConsent = useSliceStateSelector(
    (state) => state.slices.cookies.cookieConsent
  )

  const trigger = useSliceDispatch()
  const { t } = useTranslation()
  const [settings, setSettings] = useState<CookieConsent | undefined>(
    cookieConsent
  )

  const handleCloseDialog = () => {
    trigger('setCookieSettingsOpen', false)
  }

  const handleSettingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (settings) {
      setSettings({
        ...settings,
        [event.target.name]: event.target.checked,
      })
    }
  }

  const handleSaveSettings = () => {
    if (settings) {
      const settingsToSave = {
        ...settings,
        timestamp: Date.now(),
      }
      trigger('setCookieSettingsOpen', false)
      trigger('setCookieConsent', settingsToSave)

      Cookies.set(cookieName.trrCookieConsent, JSON.stringify(settingsToSave), {
        expires: 180,
        secure: true,
      })
    }
  }

  return (
    <Dialog
      open
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      disableScrollLock
    >
      <DialogTitle data-testid="cookie-settings-dialog-title">
        {t('translation:cookieSettings.title')}
        <IconButton
          aria-label={t('translation:common.close.ariaLabel')}
          onClick={handleCloseDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings?.essential}
                onChange={handleSettingChange}
                name="essential"
              />
            }
            label={t('translation:cookieSettings.essential.label')}
            disabled
          />
          <Typography mb={1}>
            {t('translation:cookieSettings.essential.description')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings?.personalization}
                onChange={handleSettingChange}
                name="personalization"
              />
            }
            label={t('translation:cookieSettings.personalization.label')}
          />
          <Typography mb={1}>
            {t('translation:cookieSettings.personalization.description')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings?.functional}
                onChange={handleSettingChange}
                name="functional"
              />
            }
            label={t('translation:cookieSettings.functional.label')}
          />
          <Typography mb={1}>
            {t('translation:cookieSettings.functional.description')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.analytics}
                onChange={handleSettingChange}
                name="analytics"
              />
            }
            label={t('translation:cookieSettings.analytics.label')}
          />
          <Typography mb={1}>
            {t('translation:cookieSettings.analytics.description')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings?.marketing}
                onChange={handleSettingChange}
                name="marketing"
                data-testid="cookie-settings-marketing"
              />
            }
            label={t('translation:cookieSettings.marketing.label')}
          />
          <Typography mb={1}>
            {t('translation:cookieSettings.marketing.description')}
          </Typography>
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCloseDialog}
          variant="outlined"
          data-testid="cookie-settings-dialog-close"
        >
          {t('translation:cookieSettings.buttons.cancel')}
        </Button>
        <Button
          onClick={handleSaveSettings}
          data-testid="cookie-settings-dialog-save"
        >
          {t('translation:cookieSettings.buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CookieSettings
